import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Box, Button, Grid, Typography } from '@mui/material';
import {
  errorColor,
  gray100,
  primaryButtonBackgroundColor
} from '../../../constants/Colors';
import { sideBarWidth } from '../../../constants/Location';
import {
  certificationFlgOptions,
  industryList
} from '../../../constants/SelectOptions';
import CustomHalfTextfield from '../../uiParts/CustomHalfTextfield';
import CustomLoading from '../../uiParts/CustomLoading';
import CustomSelectField from '../../uiParts/CustomSelectField';
import CustomSnackbar from '../../uiParts/CustomSnackbar';
import CustomTextfield from '../../uiParts/CustomTextfield';
import SideBar from '../../uiParts/SideBar';

import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import { common } from '../../../modules/error';
import { accountBasicChangeSchema } from '../../../validation/user';
import { AccountBasicChangeType } from '../../../validation/user/userType';

import { AuthContext } from '../../../contexts/auth';
import { response } from '../../../api/db';
import { getCustomerInfo, updateUser } from '../../../api/db/user';
import { AxiosError } from 'axios';
import CustomHead from '../CustomHead';
import { sentryLog } from '../../../config/sentry';
import CustomRadioButton from '../../uiParts/CustomRadioButton';

const styles = {
  button: {
    fontSize: '16px',
    fontWeight: '500',
    height: '44px',
    mt: '40px'
  },
  container: {
    width: '560px',
    '@media screen and (max-width: 960px)': {
      maxWidth: '560px',
      width: '90%'
    }
  },
  containerWrapper: {
    m: `0 0 0 ${sideBarWidth}`,
    p: '32px 0 200px',
    '@media screen and (max-width: 960px)': {
      m: `0`,
      p: '24px 0 140px'
    }
  },
  halfTextfieldWrapper: {
    '@media screen and (max-width:960px)': {
      width: '49%'
    }
  },
  label: {
    color: gray100,
    fontSize: '14px',
    mb: '12px'
  },
  labelRequired: {
    color: errorColor,
    fontSize: '14px',
    marginLeft: '8px'
  },
  pageWrapper: {
    '@media screen and (max-width: 960px)': {
      flexDirection: 'column'
    }
  },
  title: {
    color: gray100,
    fontSize: '32px',
    fontWeight: '500',
    mb: '24px',
    '@media screen and (max-width: 960px)': {
      fontSize: '24px'
    }
  }
};

const AccountBasicChange: React.FC = () => {
  const navigate = useNavigate();
  const [errorText, setErrorText] = useState('');
  const [firstSubmit, setFirstSubmit] = useState(false);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const { isLoggedIn } = useContext(AuthContext);

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
    setValue,
    watch,
    clearErrors
  } = useForm<AccountBasicChangeType>({
    resolver: yupResolver<AccountBasicChangeType>(accountBasicChangeSchema)
  });

  useEffect(() => {
    void (async () => {
      setLoading(true);
      if (!isLoggedIn) {
        navigate('/auth/login');
        return;
      }
      try {
        // ログイン中のユーザーを取得
        const response = await getCustomerInfo();
        const user = response.data;
        reset({
          company: user.company,
          companyKana: user.company_kana,
          companyShort: user.company_short,
          department: user.department,
          firstName: user.first_name,
          firstNameKana: user.first_name_kana,
          homepage: user.homepage,
          industry: user.industry,
          lastName: user.last_name,
          lastNameKana: user.last_name_kana,
          tel: user.tel,
          certificationFlg: user.certification_flg,
          certificationName: user.certification_name
        });
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        sentryLog(error);
        const axiosError = error as AxiosError;
        if (axiosError.isAxiosError && axiosError.response) {
          const errorResponse = axiosError.response as response;
          setErrorText(errorResponse.data.message);
          setOpen(true);
        } else {
          setErrorText(common);
          setOpen(true);
        }
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const buttonDisabledFlag = (): boolean => {
    if (firstSubmit) {
      return (
        watch('company') === undefined ||
        '' ||
        watch('companyKana') === undefined ||
        '' ||
        watch('companyShort') === undefined ||
        '' ||
        watch('department') === undefined ||
        '' ||
        watch('firstName') === undefined ||
        '' ||
        watch('firstNameKana') === undefined ||
        '' ||
        watch('industry') === undefined ||
        '' ||
        watch('lastName') === undefined ||
        '' ||
        watch('lastNameKana') === undefined ||
        '' ||
        watch('tel') === undefined ||
        '' ||
        watch('certificationFlg') === undefined ||
        '' ||
        watch('certificationName') === undefined ||
        '' ||
        Boolean(errors.company) ||
        Boolean(errors.companyKana) ||
        Boolean(errors.department) ||
        Boolean(errors.firstName) ||
        Boolean(errors.firstNameKana) ||
        Boolean(errors.homepage) ||
        Boolean(errors.industry) ||
        Boolean(errors.lastName) ||
        Boolean(errors.lastNameKana) ||
        Boolean(errors.tel) ||
        Boolean(errors.certificationFlg) ||
        Boolean(errors.certificationName)
      );
    }
    return false;
  };

  const onSubmit = async (): Promise<void> => {
    setLoading(true);
    try {
      // 顧客更新
      await updateUser({
        company: watch('company'),
        companyKana: watch('companyKana'),
        companyShort: watch('companyShort'),
        department: watch('department'),
        firstName: watch('firstName'),
        firstNameKana: watch('firstNameKana'),
        homepage: watch('homepage'),
        industry: watch('industry'),
        lastName: watch('lastName'),
        lastNameKana: watch('lastNameKana'),
        tel: watch('tel'),
        certificationFlg: watch('certificationFlg'),
        certificationName: watch('certificationName')
      });
      navigate('/account/basic/complete');
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      sentryLog(error);
      const axiosError = error as AxiosError;
      if (axiosError.isAxiosError && axiosError.response) {
        const errorResponse = axiosError.response as response;
        setErrorText(errorResponse.data.message);
        setOpen(true);
      } else {
        setErrorText(common);
        setOpen(true);
      }
      throw new Error('account basic change error');
    } finally {
      setLoading(false);
    }
  };

  // 取得済の場合は名称を必須にする
  const [certificationNameRequired, setCertificationNameRequired] =
    useState(false);

  // 未取得の場合は名称をdisabledにする
  const [certificationNameDisabled, setCertificationNameDisabled] =
    useState(true);

  useEffect(() => {
    if (Number(watch('certificationFlg')) === 1) {
      setCertificationNameRequired(true);
      setCertificationNameDisabled(false);
    } else if (!(Number(watch('certificationFlg')) === 1)) {
      setCertificationNameRequired(false);
      setCertificationNameDisabled(true);
      setValue('certificationName', null);
      clearErrors('certificationName');
    }
  }, [watch('certificationFlg')]);

  return (
    <>
      <Grid container sx={styles.pageWrapper}>
        <CustomHead title="基本情報変更 | CMCOS[コムコス]" />
        <SideBar />
        <Grid
          container
          direction="column"
          alignItems="center"
          item
          sx={styles.containerWrapper}
        >
          <CustomLoading loading={loading} />
          <Box sx={styles.container}>
            <Typography sx={styles.title}>基本情報変更</Typography>
            <form>
              <Controller
                render={({ field }) => (
                  <CustomTextfield
                    {...field}
                    error={Boolean(errors.company)}
                    formMb="24px"
                    helperText={errors.company?.message}
                    labelText="会社名"
                    requiredText="必須"
                  />
                )}
                control={control}
                name="company"
              />
              <Controller
                render={({ field }) => (
                  <CustomTextfield
                    {...field}
                    error={Boolean(errors.companyKana)}
                    formMb="24px"
                    helperText={errors.companyKana?.message}
                    labelText="会社名(フリガナ)"
                    requiredText="必須"
                  />
                )}
                control={control}
                name="companyKana"
              />
              <Controller
                render={({ field }) => (
                  <CustomTextfield
                    {...field}
                    error={Boolean(errors.companyShort)}
                    formMb="24px"
                    helperText={errors.companyShort?.message}
                    labelText="会社名略称(7文字以内推奨)"
                    requiredText="必須"
                  />
                )}
                control={control}
                name="companyShort"
              />
              <Controller
                render={({ field }) => (
                  <CustomTextfield
                    {...field}
                    error={Boolean(errors.department)}
                    formMb="24px"
                    helperText={errors.department?.message}
                    labelText="部署名"
                    requiredText="必須"
                  />
                )}
                control={control}
                name="department"
              />
              <Box sx={{ mb: '24px' }}>
                <Typography sx={styles.label}>
                  担当者
                  <Typography component="span" sx={styles.labelRequired}>
                    必須
                  </Typography>
                </Typography>
                <Grid container justifyContent="space-between">
                  <Grid item sx={styles.halfTextfieldWrapper}>
                    <Controller
                      render={({ field }) => (
                        <CustomHalfTextfield
                          {...field}
                          error={Boolean(errors.lastName)}
                          helperText={errors.lastName?.message}
                          labelText="姓"
                        />
                      )}
                      control={control}
                      name="lastName"
                    />
                  </Grid>
                  <Grid item sx={styles.halfTextfieldWrapper}>
                    <Controller
                      render={({ field }) => (
                        <CustomHalfTextfield
                          {...field}
                          error={Boolean(errors.firstName)}
                          helperText={errors.firstName?.message}
                          labelText="名"
                        />
                      )}
                      control={control}
                      name="firstName"
                    />
                  </Grid>
                </Grid>
              </Box>
              <Box sx={{ mb: '24px' }}>
                <Typography sx={styles.label}>
                  担当者(フリガナ)
                  <Typography component="span" sx={styles.labelRequired}>
                    必須
                  </Typography>
                </Typography>
                <Grid container justifyContent="space-between">
                  <Grid item sx={styles.halfTextfieldWrapper}>
                    <Controller
                      render={({ field }) => (
                        <CustomHalfTextfield
                          {...field}
                          error={Boolean(errors.lastNameKana)}
                          helperText={errors.lastNameKana?.message}
                          labelText="セイ"
                        />
                      )}
                      control={control}
                      name="lastNameKana"
                    />
                  </Grid>
                  <Grid item sx={styles.halfTextfieldWrapper}>
                    <Controller
                      render={({ field }) => (
                        <CustomHalfTextfield
                          {...field}
                          error={Boolean(errors.firstNameKana)}
                          helperText={errors.firstNameKana?.message}
                          labelText="メイ"
                        />
                      )}
                      control={control}
                      name="firstNameKana"
                    />
                  </Grid>
                </Grid>
              </Box>
              <Controller
                render={({ field }) => (
                  <CustomTextfield
                    {...field}
                    error={Boolean(errors.tel)}
                    formMb="24px"
                    helperText={errors.tel?.message}
                    labelText="電話番号"
                    requiredText="必須"
                  />
                )}
                control={control}
                name="tel"
              />
              <Controller
                render={({ field }) => (
                  <CustomTextfield
                    {...field}
                    error={Boolean(errors.homepage)}
                    formMb="24px"
                    helperText={errors.homepage?.message}
                    labelText="企業ホームページのURL"
                    placeholder="https://xxx.com"
                  />
                )}
                control={control}
                name="homepage"
              />
              <Controller
                render={({ field }) => (
                  <CustomSelectField
                    {...field}
                    error={Boolean(errors.industry)}
                    helperText={errors.industry?.message}
                    labelText="業種"
                    list={industryList}
                    onChange={field.onChange}
                    requiredText="必須"
                    formMb="24px"
                    value={field.value}
                  />
                )}
                control={control}
                name="industry"
              />
              <Controller
                name="certificationFlg"
                control={control}
                render={({ field }) => (
                  <CustomRadioButton
                    {...field}
                    value={field.value}
                    onChange={field.onChange}
                    options={certificationFlgOptions}
                    labelText="SBT等の企業の環境認定"
                    error={Boolean(errors.certificationFlg)}
                    helperText={errors.certificationFlg?.message}
                    formMb="24px"
                  />
                )}
              />
              <Controller
                render={({ field }) => (
                  <CustomTextfield
                    {...field}
                    error={Boolean(errors.certificationName)}
                    helperText={errors.certificationName?.message}
                    labelText="認定の名称"
                    {...(certificationNameRequired && {
                      requiredText: '必須'
                    })}
                    {...(certificationNameDisabled && {
                      disabled: true
                    })}
                  />
                )}
                control={control}
                name="certificationName"
              />
              <Button
                disabled={buttonDisabledFlag()}
                fullWidth
                onClick={() => {
                  void (async () => {
                    setFirstSubmit(true);
                    await handleSubmit(async () => await onSubmit())();
                  })();
                }}
                sx={[primaryButtonBackgroundColor, styles.button]}
                variant="contained"
              >
                変更する
              </Button>
            </form>
          </Box>
          <CustomSnackbar
            open={open}
            autoHideDuration={6000}
            onClose={() => setOpen(false)}
            text={errorText}
          />
        </Grid>
      </Grid>
    </>
  );
};

export default AccountBasicChange;
